import { AUDIENCE_TEMPLATES_CONFIG } from '../config/templates.config'
import type { AudienceRule } from '@/types/types'

export type HighlightInfo = {
  hasDefined: boolean
  templateName: string | null
  templateId: number | null
}
export type HighlightInfoFn = {
  (attribute: string): HighlightInfo
}

export default function useRulesMatcher(rules: Ref<{ [key: string]: AudienceRule }>) {
  const areValuesEqual = (value1: any, value2: any): boolean => {
    if (Array.isArray(value1) && Array.isArray(value2)) {
      if (value1.length !== value2.length) return false
      return value1.every((val, index) => val === value2[index])
    }
    return value1 === value2
  }

  const areDateFiltersEqual = (filter1: any, filter2: any): boolean => {
    if (!filter1 && !filter2) return true
    if (!filter1 || !filter2) return false

    return (
      filter1.type === filter2.type &&
      filter1.operator === filter2.operator &&
      areValuesEqual(filter1.value, filter2.value)
    )
  }

  const isRuleMatchingTemplate = (currentRule: AudienceRule, templateRule: AudienceRule): boolean => {
    if (!currentRule || !templateRule) return false

    if (currentRule.operator !== templateRule.operator) return false

    if (!areValuesEqual(currentRule.value, templateRule.value)) return false

    if (currentRule.parameters?.date_filter || templateRule.parameters?.date_filter) {
      return areDateFiltersEqual(currentRule.parameters?.date_filter, templateRule.parameters?.date_filter)
    }

    return true
  }

  const hasDefinedValue = (rule: AudienceRule): boolean => {
    if (!rule || rule.value === null || rule.value === undefined) return false

    if (Array.isArray(rule.value)) {
      return rule.value.length > 0 && rule.value.some((val) => val !== '' && val !== null && val !== undefined)
    }

    return true
  }

  const findMatchingTemplateForAttribute = (attribute: string) => {
    const currentRule = rules.value[attribute]
    if (!currentRule) return null

    if (!hasDefinedValue(currentRule)) return null

    for (const template of AUDIENCE_TEMPLATES_CONFIG) {
      const templateRule = template.rules[attribute]
      if (templateRule && isRuleMatchingTemplate(currentRule, templateRule)) {
        return template
      }
    }

    return null
  }

  const getHighlightInfo = (attribute: string) => {
    const currentRule = rules.value[attribute]
    const hasDefined = hasDefinedValue(currentRule)
    const matchedTemplate = hasDefined ? findMatchingTemplateForAttribute(attribute) : null

    return {
      hasDefined,
      templateName: matchedTemplate?.title || null,
      templateId: matchedTemplate?.id || null
    }
  }

  return {
    getHighlightInfo
  }
}
